import addMoneyMask from "./addMoneyMask";
import getFees from "./getFees";
import useConsorcioStore from "../storeModules/useConsorcioStore";
import useInitialConfigStore from "../storeModules/useInitialConfigStore";
import useInsuranceStore from "../storeModules/useInsuranceStore";
import usePaymentStore from "../storeModules/usePaymentStore";
import useUserStore from "../storeModules/useUserStore";
import { getCardText } from "../components/plan-list-carousel/components/planCard/componentUtils";
import { Catalog, tipoPlanoNomeCased } from "./constants";
import {
  CARD_PAYMENT_APPROVED_GIF_URL,
  CARD_PAYMENT_REFUSED_GIF_URL,
} from "./paymentImageUrls";
import { OPENING_BLOCK_CTA, OPENING_BLOCK_STEP } from "./openingBlockConstants";

const CATEGORY_IMAGE_BASE =
  "https://chatbot-cdn.consorciosmycon.com.br/custom/categorias";

export { OPENING_BLOCK_CTA, OPENING_BLOCK_STEP } from "./openingBlockConstants";

const PIX_QRCODE_IMAGE_API =
  "https://cd-gw.mycon.com.br/api/pix/gerar-qrcode";

export const buildPixQrcodeImageUrl = (qrCode, url) => {
  if (url) return url;
  if (!qrCode) return null;
  return `${PIX_QRCODE_IMAGE_API}?qrcodeText=${encodeURIComponent(qrCode)}`;
};

export const isSecondaryCopyStep = (stepName = "") =>
  /^copy_intermediaria_[2-9]\d*$/.test(stepName) ||
  /^copy-message-[2-9]\d*$/.test(stepName);

export const hasUnifiedOpeningBlock = (items = []) =>
  items.some((item) => item.type === "banner");

export const getFilteredPlansByTipoTabela = (plan, tipoTabela) =>
  (plan.planosTipos ?? []).filter((tipo) => {
    if (!tipo.name) return false;

    switch (tipoTabela) {
      case "integral_pagametade":
        return (
          tipo.name.toUpperCase() ===
            tipoPlanoNomeCased.CREDITO_INTEGRAL.toUpperCase() ||
          tipo.name.toUpperCase() ===
            tipoPlanoNomeCased.CREDITO_PAGAMETADE.toUpperCase()
        );
      case "integral_reduzida":
        return (
          tipo.name.toUpperCase() ===
            tipoPlanoNomeCased.CREDITO_INTEGRAL.toUpperCase() ||
          tipo.name.toUpperCase() === tipoPlanoNomeCased.PAGA_REDUZIDA.toUpperCase()
        );
      case "reduzida":
        return (
          tipo.name.toUpperCase() === tipoPlanoNomeCased.PAGA_REDUZIDA.toUpperCase()
        );
      case "pagametade_reduzida":
        return (
          tipo.name.toUpperCase() ===
            tipoPlanoNomeCased.CREDITO_PAGAMETADE.toUpperCase() ||
          tipo.name.toUpperCase() === tipoPlanoNomeCased.PAGA_REDUZIDA.toUpperCase()
        );
      default:
        return (
          tipo.name.replace(" ", "").toUpperCase() ===
          String(tipoTabela).replace("_", " ").toUpperCase()
        );
    }
  });

const isIntegralPlan = (name = "") =>
  name.toUpperCase() === tipoPlanoNomeCased.CREDITO_INTEGRAL.toUpperCase() ||
  name === "Integral";

const isPagaMetadePlan = (name = "") =>
  name.toUpperCase() === tipoPlanoNomeCased.CREDITO_PAGAMETADE.toUpperCase() ||
  name === "Paga Metade";

const isPagaReduzidaPlan = (name = "") =>
  name.toUpperCase() === tipoPlanoNomeCased.PAGA_REDUZIDA.toUpperCase() ||
  name === "Paga Reduzida";

const findTipo = (tipos, matcher) =>
  tipos.find((entry) => matcher(entry.name)) ?? null;

const buildPlanTipoDetails = (tipo) => {
  if (!tipo) {
    return { taxa: null, mensagem: null };
  }

  return {
    taxa: getFees(tipo)?.value ?? null,
    mensagem: getCardText(tipo.name),
  };
};

export const buildCategoryCardsItem = () => {
  const categories = useConsorcioStore.getState().filteredCategories ?? [];

  return {
    type: "card",
    message: categories.map((category) => ({
      nome: category.text,
      codigo: Catalog.category[category.categoriaID] ?? "",
      imagemUrl:
        category.imagemUrl ||
        `${CATEGORY_IMAGE_BASE}/${category.categoriaID}.svg`,
      categoriaID: category.categoriaID,
    })),
  };
};

export const buildPlanCardsItem = () => {
  const { fetchedPlans } = useConsorcioStore.getState();
  const { tipoTabela } = useInitialConfigStore.getState();

  const message = (fetchedPlans ?? [])
    .filter((plan) => plan.planoCardType !== "simular-novamente")
    .map((plan) => {
      const tipos = getFilteredPlansByTipoTabela(plan, tipoTabela);
      const tipoIntegral = findTipo(tipos, isIntegralPlan);
      const tipoPagaMetade = findTipo(tipos, isPagaMetadePlan);
      const tipoPagaReduzida = findTipo(tipos, isPagaReduzidaPlan);
      const valorIntegral = tipoIntegral?.valorParcela ?? null;
      const valorPagaMetade = tipoPagaMetade?.valorParcela ?? null;
      const valorParcelaReduzida = tipoPagaReduzida?.valorParcela ?? null;
      const integralDetails = buildPlanTipoDetails(tipoIntegral);
      const pagaMetadeDetails = buildPlanTipoDetails(tipoPagaMetade);
      const pagaReduzidaDetails = buildPlanTipoDetails(tipoPagaReduzida);

      return {
        meses: `${plan.planoMeses} meses`,
        credito: addMoneyMask(plan.bemValor ?? plan.bemCredito),
        integral: valorIntegral != null,
        paga_metade: valorPagaMetade != null,
        valor_integral:
          valorIntegral != null ? addMoneyMask(valorIntegral) : null,
        taxa_integral: integralDetails.taxa,
        mensagem_integral: integralDetails.mensagem,
        parcela_reduzida: valorParcelaReduzida != null,
        valor_paga_metade:
          valorPagaMetade != null ? addMoneyMask(valorPagaMetade) : null,
        taxa_paga_metade: pagaMetadeDetails.taxa,
        mensagem_paga_metade: pagaMetadeDetails.mensagem,
        valor_parcela_reduzida:
          valorParcelaReduzida != null
            ? addMoneyMask(valorParcelaReduzida)
            : null,
        taxa_parcela_reduzida: pagaReduzidaDetails.taxa,
        mensagem_parcela_reduzida: pagaReduzidaDetails.mensagem,
      };
    })
    .filter(
      (card) => card.integral || card.paga_metade || card.parcela_reduzida
    );

  return { type: "card_plans", message };
};

export const buildInsuranceCardsItem = () => {
  const { valorPremio } = useInsuranceStore.getState();
  const { selectedPlano } = useConsorcioStore.getState();
  const { insuranceMonthlyParcel } = usePaymentStore.getState();

  const valorParcela = selectedPlano?.planoValorParcela ?? 0;
  const valorAcrescimo = selectedPlano?.valorPremioSeguro || valorPremio || 0;
  const valorComSeguro =
    insuranceMonthlyParcel || valorParcela + valorAcrescimo;

  return {
    type: "cards_seguros",
    message: [
      {
        opcao: "COM SEGURO",
        valor_mensal: addMoneyMask(valorComSeguro),
        valor_acrescimo: addMoneyMask(valorAcrescimo),
      },
      {
        opcao: "SEM SEGURO",
        valor_mensal: addMoneyMask(valorParcela),
      },
    ],
  };
};

const buildPixCardMessage = (tipo) => {
  const pix = useUserStore.getState().payment?.pix ?? {};
  const { qrCode, url, valor } = pix;

  if (tipo === "qrcode" && !url && !qrCode) return null;
  if (tipo === "copia_cola" && !qrCode) return null;

  const message = {
    tipo,
    codigo: qrCode ?? null,
    imagem_url:
      tipo === "qrcode" ? buildPixQrcodeImageUrl(qrCode, url) : null,
  };

  if (valor != null && valor !== "") {
    message.valor =
      typeof valor === "number" ? addMoneyMask(valor) : String(valor);
  }

  return message;
};

export const buildPixQrcodeCardItem = () => {
  const message = buildPixCardMessage("qrcode");
  if (!message) return null;

  return { type: "card_pix", message };
};

export const buildPixCopyPasteCardItem = () => {
  const message = buildPixCardMessage("copia_cola");
  if (!message) return null;

  return { type: "card_pix", message };
};

export const getPixDataFromCardItem = (item) => {
  if (item?.type !== "card_pix" || !item.message) return null;

  const { codigo, imagem_url, valor } = item.message;

  return {
    qrCode: codigo ?? undefined,
    url: buildPixQrcodeImageUrl(codigo, imagem_url) ?? undefined,
    valor: valor ?? undefined,
  };
};

const getBoletoPaymentData = () => {
  const boleto = useUserStore.getState().payment?.boleto ?? {};

  return {
    codigoBarras:
      boleto.boletoCodigoBarras ??
      boleto.BoletoCodigoBarras ??
      boleto.codigoBarras ??
      null,
    url: boleto.boletoURL ?? boleto.BoletoURL ?? boleto.boletoUrl ?? null,
    vencimento:
      boleto.boletoVencimento ??
      boleto.BoletoVencimento ??
      boleto.vencimento ??
      null,
    valor: boleto.valor ?? boleto.Valor ?? null,
  };
};

const buildBoletoCardMessage = (tipo) => {
  const { codigoBarras, url, vencimento, valor } = getBoletoPaymentData();

  if (!codigoBarras && !url) return null;

  const message = {
    tipo,
    codigo_barras: codigoBarras,
    url,
    vencimento: vencimento != null ? String(vencimento) : null,
  };

  if (valor != null && valor !== "") {
    message.valor =
      typeof valor === "number" ? addMoneyMask(valor) : String(valor);
  }

  return message;
};

export const buildBoletoOptionsCardItem = () => {
  const message = buildBoletoCardMessage("opcoes");
  if (!message) return null;

  return { type: "card_boleto", message };
};

export const buildBoletoBarCodeCardItem = () => {
  const message = buildBoletoCardMessage("codigo_barras");
  if (!message) return null;

  return { type: "card_boleto", message };
};

export const getBoletoDataFromCardItem = (item) => {
  if (item?.type !== "card_boleto" || !item.message) return null;

  const { codigo_barras, url, vencimento, valor } = item.message;

  return {
    boletoCodigoBarras: codigo_barras ?? undefined,
    boletoURL: url ?? undefined,
    boletoVencimento: vencimento ?? undefined,
    valor: valor ?? undefined,
  };
};

export const buildPaymentRefusedImageItem = () => ({
  type: "card_image",
  message: {
    tipo: "pagamento_recusado",
    imagem_url: CARD_PAYMENT_REFUSED_GIF_URL,
  },
});

export const buildPaymentApprovedImageItem = () => ({
  type: "card_image",
  message: {
    tipo: "pagamento_aprovado",
    imagem_url: CARD_PAYMENT_APPROVED_GIF_URL,
  },
});

export const getPaymentImageUrlFromCardItem = (item) => {
  if (item?.type !== "card_image" || !item.message?.imagem_url) return null;

  return item.message.imagem_url;
};

export const buildOpeningBlockItems = () => {
  const { bannerSrc, initialCopys } = useInitialConfigStore.getState();
  const items = [];

  if (bannerSrc) {
    items.push({
      type: "banner",
      message: { imagem_url: bannerSrc },
    });
  }

  (initialCopys ?? []).forEach((copy) => {
    const mensagem = copy?.mensagem;
    if (mensagem) {
      items.push({ type: "text", message: String(mensagem) });
    }
  });

  if ((initialCopys ?? []).length > 1) {
    items.push({
      type: "button",
      message: [{ "tx-button": OPENING_BLOCK_CTA }],
    });
  }

  return items;
};

export const historicoHasUnifiedOpeningBlock = (historico = []) =>
  historico.some(
    (entry) =>
      entry.step === OPENING_BLOCK_STEP &&
      entry.sender === "bot" &&
      hasUnifiedOpeningBlock(entry.items ?? [])
  );

const hasStructuredMessage = (message) => {
  if (Array.isArray(message)) return message.length > 0;

  if (message && typeof message === "object") {
    return Object.values(message).some((value) => value != null && value !== "");
  }

  return Boolean(message);
};

const STRUCTURED_ITEM_BUILDERS = {
  "escolha_categoria": buildCategoryCardsItem,
  "mensagem_carrossel_planos": buildPlanCardsItem,
  "escolha_seguro": buildInsuranceCardsItem,
  "qrcode_pix": buildPixQrcodeCardItem,
  "exibe_codigo_pix": buildPixCopyPasteCardItem,
  "opcoes_boleto": buildBoletoOptionsCardItem,
  "exibe_codigo_boleto": buildBoletoBarCodeCardItem,
  "cartao_recusado": buildPaymentRefusedImageItem,
  "pagamento_aprovado": buildPaymentApprovedImageItem,
};

export const getStructuredItemsForStep = (stepName) => {
  if (stepName === OPENING_BLOCK_STEP) {
    const items = buildOpeningBlockItems();
    return items.length > 0 ? items : [];
  }

  const builder = STRUCTURED_ITEM_BUILDERS[stepName];
  if (!builder) return [];

  const item = builder();
  if (!item || !hasStructuredMessage(item.message)) return [];

  return [item];
};

export const STEPS_WITHOUT_BUTTON_OPTIONS = new Set([
  "escolha_categoria",
  "mensagem_carrossel_planos",
]);

export const stepRequiresStructuredTracking = (stepName) =>
  getStructuredItemsForStep(stepName).length > 0;
